import React, { useState, useEffect, useRef } from 'react'
import { 
  X, Send, User, MessageCircle, Clock, 
  Loader, AlertCircle 
} from 'lucide-react'
import { conversationsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const ChatDialog = ({ isOpen, onClose, creator, project }) => {
  const { user } = useAuth()
  const [conversation, setConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
  }

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom()
    }
  }, [messages])

  // Body-scroll blokkeren zolang de dialoog open is (mobiel: anders scrolt de pagina eronder mee)
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && creator && user) {
      initializeConversation()
    }
  }, [isOpen, creator, user])

  const initializeConversation = async () => {
    if (!creator || !user) return

    try {
      setLoading(true)
      setError('')

      // Try to create or find existing conversation
      const response = await conversationsAPI.createConversation({
        participantId: creator.id,
        projectId: project?.id
      })

      setConversation(response.data.conversation)
      
      // If conversation has messages, fetch them
      if (response.data.conversation.messages && response.data.conversation.messages.length > 0) {
        setMessages(response.data.conversation.messages)
      } else {
        setMessages([])
      }
    } catch (error) {
      console.error('Error initializing conversation:', error)
      setError('Er is een fout opgetreden bij het laden van het gesprek')
    } finally {
      setLoading(false)
    }
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !conversation || sending) return

    try {
      setSending(true)
      const messageContent = newMessage.trim()
      setNewMessage('')

      const response = await conversationsAPI.sendMessage(conversation.id, {
        content: messageContent
      })

      setMessages(prev => [...prev, response.data.message])
    } catch (error) {
      console.error('Error sending message:', error)
      setError('Bericht kon niet worden verzonden')
      // Restore message if failed
      setNewMessage(newMessage)
    } finally {
      setSending(false)
    }
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now - date) / (1000 * 60 * 60)

    if (diffInHours < 1) {
      return 'Zojuist'
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
    } else {
      return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-2xl shadow-2xl max-w-2xl w-full h-full sm:h-auto max-h-none sm:max-h-[80vh] flex flex-col border border-gray-200 text-[#0F172A]">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <MessageCircle className="w-6 h-6 text-[#3EB489] flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-[#0F172A] truncate">
                Chat met {creator?.name || 'Creator'}
              </h3>
              {project && (
                <p className="text-sm text-[#475569] truncate">
                  Over: {project.title}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 -mr-1 flex-shrink-0 hover:bg-gray-100 rounded-lg transition text-[#475569] hover:text-[#0F172A]"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-hide p-4 sm:p-6 space-y-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="flex items-center gap-2 text-[#475569]">
                <Loader className="w-5 h-5 animate-spin" />
                <span>Gesprek laden...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-32">
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-white border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-[#3EB489]" />
              </div>
              <p className="text-[#475569] mb-2">Nog geen berichten</p>
              <p className="text-sm text-[#94A3B8]">
                Start het gesprek door een bericht te sturen
              </p>
            </div>
          ) : (
            <>
              {messages.map((message, index) => {
                // Determine if message is from current user
                const isMyMessage = message.sender.isMe || 
                                  (message.sender.id && user?.id && message.sender.id === user.id)
                
                return (
                  <div
                    key={message.id || index}
                    className={`flex gap-3 ${!isMyMessage ? 'flex-row-reverse' : ''}`}
                  >
                    <div className="flex-shrink-0">
                      {message.sender.profilePictureUrl ? (
                        <img
                          src={message.sender.profilePictureUrl}
                          alt={message.sender.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#3EB489] flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                    <div className={`min-w-0 max-w-[85%] sm:max-w-xs ${!isMyMessage ? 'text-right' : ''}`}>
                      <div
                        className={`inline-block text-left px-4 py-2 rounded-2xl ${
                          isMyMessage
                            ? 'bg-white border border-gray-200 text-[#0F172A]'
                            : 'bg-[#3EB489] text-white'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{message.content}</p>
                      </div>
                      <div className={`flex items-center gap-1 mt-1 text-xs text-[#94A3B8] ${!isMyMessage ? 'justify-end' : ''}`}>
                        <Clock className="w-3 h-3" />
                        <span>{formatTime(message.sentAt)}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Message Input */}
        {conversation && (
          <form onSubmit={handleSendMessage} className="p-3 sm:p-6 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-6 border-t border-gray-100 shrink-0 bg-white">
            <div className="flex gap-2 sm:gap-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type een bericht..."
                className="flex-1 min-w-0 px-4 py-3 bg-white border border-gray-200 rounded-xl focus:border-[#3EB489] focus:outline-none transition text-base text-[#0F172A] placeholder-[#94A3B8]"
                disabled={sending}
                maxLength={2000}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="px-4 sm:px-6 py-3 min-w-[48px] justify-center bg-[#3EB489] hover:bg-[#359E78] disabled:bg-gray-100 disabled:text-[#94A3B8] rounded-xl transition flex items-center gap-2 text-white font-medium flex-shrink-0"
              >
                {sending ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Verzend</span>
              </button>
            </div>
            {newMessage.length > 1800 && (
              <p className="text-xs text-[#475569] mt-1">
                {2000 - newMessage.length} karakters over
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  )
}

export default ChatDialog